import { ArrowLeftOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import { Button, Divider, Empty, Modal, Space, Switch, Tag, Tooltip, Typography } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import errorAudioFile from '~/assets/audio/error.mp3';
import successAudioFile from '~/assets/audio/success.mp3';
import warningAudioFile from '~/assets/audio/warning.mp3';
import api from '~/services/api';
import history from '~/services/history';

import { Container, ContainerForTables, SearchContainer, SearchSummary, StyledButton, TransactionContainer, TransactionTable } from './styles';
import VirtualTable from './VirtualTable';

const { confirm } = Modal;
const { Title } = Typography;

const statusColor = {
  'Not Scanned': 'default',
  'Not Found': 'red',
  'Item not in RBMS': 'red',
  'Different Location': 'orange',
  'Marked not found previously': 'orange',
  'TBI for more than 30 days': 'orange',
  OK: 'green',
  'OK-AAPIE': 'green',
  'Not in RBMS': 'purple',
  'Incorrect Status': 'red',
};

// Audio helper
const audioCache = {};
function playAudio(file) {
  if (!audioCache[file]) audioCache[file] = new Audio(file);
  audioCache[file].currentTime = 0;
  audioCache[file].play().catch((error) => {
    console.warn('Audio play failed:', error);
  });
}

export default function PhysicalInventoryLocationItems({ match, location }) {
  const [itemsList, setItemsList] = useState([]);
  const [locationInfo, setLocationInfo] = useState(null);
  const [locationStatus, setLocationStatus] = useState(null);
  const [physicalLocationStatus, setPhysicalLocationStatus] = useState(null);
  const [, setCounterWrongLocation] = useState(0);
  const [assignedTechnician, setAssignedTechnician] = useState('');
  const [disableSerialNumberInput, setDisableSerialNumberInput] = useState(false);
  const [inventoryManagerId, setInventoryManagerId] = useState(null);
  const [scannedLevel, setScannedLevel] = useState({ toLocationId: null, toLocationName: '' });
  const [isParentLocation, setIsParentLocation] = useState(false);
  const [levelSummary, setLevelSummary] = useState([]);
  const [parentLocationLevels, setParentLocationLevels] = useState([]);
  const [timesScannedSummary, setTimesScannedSummary] = useState([]);
  const [highlightedRow, setHighlightedRow] = useState(null);
  const [includeNotScanned, setIncludeNotScanned] = useState(false);
  const [isLoadingItems, setIsLoadingItems] = useState(false);

  const profile = useSelector((state) => state.user.profile);
  const params = new URLSearchParams(location.search);
  const returnUrl = params.get('return') === 'dashboard' ? '/physicalInventoryLocationsDashboard' : '/physicalInventoryLocations';

  const inputSearchRef = useRef(null);
  const virtualTableRef = useRef(null);
  const itemsListRef = useRef(itemsList);

  useEffect(() => {
    itemsListRef.current = itemsList;
  }, [itemsList]);

  const loadItems = useCallback(async () => {
    setIsLoadingItems(true);
    try {
      const locationItems = await api.get(`/physical_inventory/locationItems/${match.params.physicalInventoryLocationId}`, {
        params: includeNotScanned ? { includeNotScanned: true } : undefined,
      });
      const sortedItems = [...locationItems.data.items].sort((a, b) => a.serial_number.localeCompare(b.serial_number));

      setPhysicalLocationStatus(locationItems.data.status);

      setItemsList(sortedItems);
      setLocationInfo({
        locationName: locationItems.data.name,
        locationDescription: locationItems.data.description,
        locationId: locationItems.data.id,
      });
      setLocationStatus(locationItems.data.status === 'Complete');
      setAssignedTechnician(locationItems.data.user || '');
      setDisableSerialNumberInput(locationItems.data.user !== profile.name);
      setInventoryManagerId(locationItems.data.inventory_manager_id);

      const timesScannedData = locationItems.data.items
        .filter((item) => item.times_scanned > 1)
        .map((item) => ({ serial_number: item.serial_number, times_scanned: item.times_scanned }));
      setTimesScannedSummary(timesScannedData);

      if (locationItems.data.is_parent) {
        setIsParentLocation(locationItems.data.is_parent || false);
        setParentLocationLevels(locationItems.data.levels);
      } else {
        setScannedLevel({ toLocationId: locationItems.data.id, toLocationName: locationItems.data.name });
      }
    } catch (error) {
      toast.error('Failed to load items');
      console.error(error);
    } finally {
      setIsLoadingItems(false);
    }
  }, [includeNotScanned, match.params.physicalInventoryLocationId, profile.name]);

  // Initial load and reload when includeNotScanned changes
  useEffect(() => {
    loadItems();
    if (inputSearchRef.current) {
      inputSearchRef.current.focus();
    }
  }, [loadItems, match.params.physicalInventoryId]);

  // Derived summary values
  const searchSummary = useMemo(
    () =>
      itemsList
        .filter((el) => includeNotScanned || el.status !== 'Not Scanned')
        .reduce((acc, curr) => {
          acc[curr.status] = (acc[curr.status] || 0) + 1;
          return acc;
        }, {}),
    [includeNotScanned, itemsList]
  );

  const scannedCount = useMemo(() => itemsList.filter((item) => item.status !== 'Not Scanned').length, [itemsList]);
  const hasStatusSummary = useMemo(() => Object.keys(searchSummary).length > 0, [searchSummary]);

  // Level summary effect
  useEffect(() => {
    if (!isParentLocation) return;

    const levelsData = parentLocationLevels.map((level) => ({ name: level.name, scanned: 0, rbms: 0 }));

    itemsList.forEach((item) => {
      const levelInfo = levelsData.find((level) => level.name === item['inventory_items.locations.name']);
      const scanned = item.status !== 'Not Scanned' ? 1 : 0;
      if (!levelInfo) {
        levelsData.unshift({ name: item['inventory_items.locations.name'], scanned, rbms: 1 });
      } else {
        levelInfo.scanned += scanned;
        levelInfo.rbms += 1;
      }
    });

    setLevelSummary(levelsData);
  }, [isParentLocation, itemsList, parentLocationLevels]);

  // Modal helpers
  const errorModal = useCallback(({ title, content }) => {
    Modal.error({ title, content });
  }, []);

  const warningModal = useCallback(({ title, content }) => {
    Modal.warning({ title, content });
  }, []);

  const errorMessage = useCallback(
    (title, content) => {
      playAudio(errorAudioFile);
      errorModal({ title, content });
      if (inputSearchRef.current) {
        inputSearchRef.current.value = '';
        inputSearchRef.current.focus();
      }
    },
    [errorModal]
  );

  // Delete handler
  const handleDeleteSerialNumber = useCallback(async (itemId) => {
    try {
      await api.delete(`/physical_inventory/deleteSerialNumber/${itemId}`);
      setItemsList((state) => state.filter((el) => el.id !== itemId));
      if (inputSearchRef.current) {
        inputSearchRef.current.focus();
      }
    } catch (error) {
      toast.error('Failed to delete item');
    }
  }, []);

  // Highlight and scroll to row
  const highlightRow = useCallback((serialNumber) => {
    setHighlightedRow(serialNumber);

    // Scroll to the highlighted row
    if (virtualTableRef.current) {
      virtualTableRef.current.scrollToRow(serialNumber);
    }

    // Remove highlight after 5 seconds
    setTimeout(() => setHighlightedRow(null), 5000);
  }, []);

  // Table columns
  const columns = useMemo(() => {
    const cols = [
      {
        title: 'Serial Number',
        dataIndex: 'serial_number',
        key: 'serial_number',
        width: '25%',
        render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>,
      },
      {
        title: 'Scanning Status',
        dataIndex: 'status',
        key: 'status',
        width: '12%',
        render: (status) => (status ? <Tag color={statusColor[status]}>{status}</Tag> : null),
      },
      {
        title: 'Scanned Date',
        dataIndex: 'scan_date',
        key: 'scan_date',
        width: '10%',
        render: (date) => {
          if (!date) return '';
          return new Intl.DateTimeFormat('en-CA', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            timeZone: 'America/Toronto',
          }).format(new Date(date));
        },
      },
      {
        title: 'Item Current Status on the System',
        dataIndex: 'inventory_items.status',
        key: 'inventory_items.status',
        width: '18%',
        render: (status) => {
          const statusMap = {
            Allocated: <Tag color="default">Allocated</Tag>,
            AwaitingShipment: <Tag color="warning">Awaiting Shipment</Tag>,
            Downstream: <Tag color="error">Downstream</Tag>,
            Failed: <Tag color="error">Failed</Tag>,
            ForParts: <Tag color="error">For Parts</Tag>,
            InStock: <Tag color="default">In Stock</Tag>,
            InternalAsset: <Tag color="default">InternalAsset</Tag>,
            InUse: <Tag color="default">InUse</Tag>,
            NotFound: <Tag color="error">Not Found</Tag>,
            Sold: <Tag color="default">Sold</Tag>,
            Gift: <Tag color="default">Gift</Tag>,
            'ToBeInspected-RMA': <Tag color="warning">To Be Inspected-RMA</Tag>,
            Returning: <Tag color="warning">Returning</Tag>,
            ToBeInspected: <Tag color="warning">To Be Inspected</Tag>,
          };
          return statusMap[status] || null;
        },
      },
      {
        title: 'Notes',
        dataIndex: 'notes',
        key: 'notes',
        width: '20%',
        render: (notes) => <div style={{ fontSize: 'medium' }}>{notes}</div>,
      },
      {
        title: 'Actions',
        key: 'actions',
        width: '8%',
        render: (_, record) =>
          (record.status === 'Item not in RBMS' || record.location_changed) && (
            <StyledButton onClick={() => handleDeleteSerialNumber(record.id)}>Delete</StyledButton>
          ),
      },
    ];

    if (isParentLocation) {
      cols.splice(1, 0, {
        title: 'Level',
        dataIndex: 'inventory_items.locations.name',
        key: 'inventory_items.locations.name',
        width: '15%',
      });
    }

    return cols;
  }, [isParentLocation, handleDeleteSerialNumber]);

  // Row className for highlighting
  const getRowClassName = useCallback((record) => (record.serial_number === highlightedRow ? 'highlighted' : ''), [highlightedRow]);

  const upsertScannedItem = useCallback((serialNumber, data) => {
    setItemsList((prev) => {
      const serial = (serialNumber || '').trim().toUpperCase();
      const existingIndex = prev.findIndex((item) => (item.serial_number || '').trim().toUpperCase() === serial);

      if (existingIndex >= 0) {
        return prev.map((item, index) => (index === existingIndex ? { ...item, ...data } : item));
      }

      return [
        ...prev,
        {
          id: data.id,
          serial_number: serial,
          ...data,
        },
      ];
    });
  }, []);

  const getSystemItemStatus = useCallback((scanResponseData, foundResponseData, serialNumber) => {
    const serial = (serialNumber || '').trim().toUpperCase();
    const existingItem = itemsListRef.current.find((item) => (item.serial_number || '').trim().toUpperCase() === serial);

    return (
      scanResponseData?.item_status_when_scanned ||
      scanResponseData?.['inventory_items.status'] ||
      scanResponseData?.inventory_items_status ||
      scanResponseData?.inventory_item_status ||
      scanResponseData?.item_status ||
      foundResponseData?.['inventory_items.status'] ||
      foundResponseData?.inventory_items_status ||
      foundResponseData?.inventory_item_status ||
      foundResponseData?.item_status ||
      scanResponseData?.item_status_when_incorrect_status_scanned ||
      existingItem?.['inventory_items.status'] ||
      null
    );
  }, []);

  // Scan handler
  async function handleScanSerialNumber(serialNumberScanned) {
    try {
      if (!serialNumberScanned) {
        if (inputSearchRef.current) {
          inputSearchRef.current.focus();
        }
        toast.error('Serial number cannot be empty');
        return;
      }

      const serialNumberTrimmed = serialNumberScanned.trim().toUpperCase();
      const serialNumberFormatted =
        serialNumberTrimmed.substring(0, 4) === 'P/N:'
          ? serialNumberTrimmed.substring(serialNumberTrimmed.indexOf('S/N:') + 4).toLocaleUpperCase()
          : serialNumberTrimmed.toLocaleUpperCase();

      const itemIsPartNumber = await api.post('physical_inventory/checkIsPartNumber', { serialNumber: serialNumberFormatted });

      if (itemIsPartNumber.data.isPartNumber) {
        playAudio(errorAudioFile);
        errorModal({
          title: 'Invalid Serial Number',
          content: (
            <div>
              The bar code scanned is a <span style={{ fontWeight: 'bold', fontSize: 'large' }}>PART NUMBER</span>. Please, scan again.
            </div>
          ),
        });
        if (inputSearchRef.current) {
          inputSearchRef.current.value = '';
          inputSearchRef.current.focus();
        }
        return;
      }

      const itemFoundInInventory = await api.post(`/physical_inventory/checkSerialNumber/${match.params.physicalInventoryId}`, {
        serialNumber: serialNumberFormatted,
      });

      if (itemFoundInInventory.data.status === 'Not in RBMS, already scanned') {
        playAudio(errorAudioFile);
        errorModal({
          title: 'Item not in RBMS',
          content: (
            <span>
              This serial number is not in RBMS, but it was already scanned at <strong>{itemFoundInInventory.data.location}</strong>. Put this item aside and
              inform inventory manager
            </span>
          ),
        });
        return;
      }

      if (itemFoundInInventory.data.status === 'Not Found') {
        playAudio(errorAudioFile);
        confirm({
          title: (
            <span>
              Are you sure the right Serial Number was scanned?{' '}
              <span style={{ fontWeight: 'bold', fontSize: 'large', color: 'red' }}>&lsquo;{serialNumberScanned}&rsquo;</span>
            </span>
          ),
          icon: <ExclamationCircleFilled />,
          content: `This record doesn't match any of the Serial Numbers in the RBMS. Make sure you are scanning the right serial number on the unit. If the problem persists, put this unit aside, and bring it to the attention of the inventory manager.`,
          okText: `Yes, I'm sure`,
          okType: 'success',
          cancelText: 'No, I will scan again',
          cancelButtonProps: { type: 'danger' },
          autoFocusButton: 'cancel',
          async onOk() {
            try {
              const itemScanned = await api.post(
                `/physical_inventory/${match.params.physicalInventoryId}/addSerialNumberToLocation/${match.params.physicalInventoryLocationId}`,
                { serialNumber: serialNumberFormatted, toLocationId: scannedLevel.toLocationId }
              );

              if (itemScanned.data.status === 'Item not in RBMS') {
                const itemNotInRbms = {
                  id: itemScanned.data.id,
                  serial_number: serialNumberFormatted,
                  status: itemScanned.data.status,
                  scan_date: itemScanned.data.scan_date,
                  notes: itemScanned.data.notes,
                  'inventory_items.status': getSystemItemStatus(itemScanned.data, itemFoundInInventory.data, serialNumberFormatted),
                  'inventory_items.locations.name': scannedLevel.toLocationName,
                };

                setItemsList((prev) => [...prev, itemNotInRbms]);
                highlightRow(serialNumberFormatted);

                if (inputSearchRef.current) {
                  inputSearchRef.current.value = '';
                  inputSearchRef.current.focus();
                }

                if (physicalLocationStatus === 'Pending') {
                  setPhysicalLocationStatus('Started');
                }
                return;
              }

              upsertScannedItem(serialNumberFormatted, {
                id: itemScanned.data.id,
                status: itemScanned.data.status,
                scan_date: itemScanned.data.scan_date,
                notes: itemScanned.data.notes,
                location_changed: itemScanned.data.location_changed,
                'inventory_items.status': getSystemItemStatus(itemScanned.data, itemFoundInInventory.data, serialNumberFormatted),
                'inventory_items.locations.name': scannedLevel.toLocationName,
              });
              highlightRow(serialNumberFormatted);

              if (inputSearchRef.current) {
                inputSearchRef.current.value = '';
                inputSearchRef.current.focus();
              }
              setLocationStatus(false);
            } catch (error) {
              toast.error(`Something went wrong. ${error.message}`);
            }
          },
          onCancel() {
            Modal.destroyAll();
            if (inputSearchRef.current) {
              inputSearchRef.current.value = '';
              inputSearchRef.current.focus();
            }
          },
        });
        return;
      }

      const itemScanned = await api.post(
        `/physical_inventory/${match.params.physicalInventoryId}/addSerialNumberToLocation/${match.params.physicalInventoryLocationId}`,
        { serialNumber: itemFoundInInventory.data.serial_number, toLocationId: scannedLevel.toLocationId }
      );

      if (itemScanned.data.alreadyScanned) {
        setTimesScannedSummary((state) => {
          const existing = state.find((item) => item.serial_number === itemFoundInInventory.data.serial_number);
          if (existing) {
            return state.map((item) =>
              item.serial_number === itemFoundInInventory.data.serial_number ? { ...item, times_scanned: itemScanned.data.times_scanned } : item
            );
          }
          return [...state, { serial_number: itemFoundInInventory.data.serial_number, times_scanned: itemScanned.data.times_scanned }];
        });
        playAudio(warningAudioFile);
        toast.warn('Item already scanned');
        if (inputSearchRef.current) {
          inputSearchRef.current.value = '';
          inputSearchRef.current.focus();
        }
        return;
      }

      const focusAndClear = () => {
        if (inputSearchRef.current) {
          inputSearchRef.current.value = '';
          inputSearchRef.current.focus();
        }
      };

      const { status, scan_date, notes, location_changed } = itemScanned.data;
      const scannedSerial = itemFoundInInventory.data.serial_number;
      const commonData = {
        status,
        scan_date,
        notes,
        location_changed,
        'inventory_items.status': getSystemItemStatus(itemScanned.data, itemFoundInInventory.data, scannedSerial),
        'inventory_items.locations.name': scannedLevel.toLocationName,
      };

      if (status === 'Marked not found previously') {
        playAudio(errorAudioFile);
        errorModal({
          title: status,
          content: (
            <div>
              Item status was <span style={{ fontWeight: 'bold', fontSize: 'large' }}>Not Found</span> and it was changed to TBI. Put this item aside and inform
              inventory manager
            </div>
          ),
        });
        upsertScannedItem(scannedSerial, { id: itemScanned.data.id, ...commonData });
        highlightRow(scannedSerial);
        focusAndClear();
        return;
      }

      if (status === 'Incorrect Status') {
        playAudio(errorAudioFile);
        errorModal({
          title: status,
          content: (
            <div>
              Item status is <span style={{ fontWeight: 'bold', fontSize: 'large' }}>{itemScanned.data.item_status_when_incorrect_status_scanned}</span>. Put
              this item aside and inform inventory manager
            </div>
          ),
        });
        upsertScannedItem(scannedSerial, { id: itemScanned.data.id, ...commonData });
        highlightRow(scannedSerial);
        focusAndClear();
        return;
      }

      if (status === 'TBI for more than 30 days') {
        playAudio(errorAudioFile);
        errorModal({
          title: status,
          content: (
            <div>
              This item is marked as <span style={{ fontWeight: 'bold', fontSize: 'large' }}>TBI for more than 30 days</span>.
            </div>
          ),
        });
        upsertScannedItem(scannedSerial, { id: itemScanned.data.id, ...commonData });
        highlightRow(scannedSerial);
        focusAndClear();
        return;
      }

      if (status === 'OK' || status === 'OK-AAPIE') {
        playAudio(successAudioFile);

        const currentItems = itemsListRef.current;
        const exists = currentItems.some((item) => item.serial_number === scannedSerial);

        if (!exists) {
          setItemsList((prev) => [
            ...prev,
            {
              id: itemScanned.data.id,
              serial_number: scannedSerial,
              ...commonData,
            },
          ]);
          highlightRow(scannedSerial);

          if (location_changed !== null) {
            setCounterWrongLocation((prev) => {
              const next = prev + 1;
              if (next === 3) {
                playAudio(errorAudioFile);
                warningModal({
                  title: 'Possible Wrong Location',
                  content: `Are you scanning in the right location? Current location: ${locationInfo?.locationDescription}`,
                });
              }
              return next;
            });
          }

          setLocationStatus(false);
          if (physicalLocationStatus === 'Pending') {
            setPhysicalLocationStatus('Started');
          }
          focusAndClear();
          return;
        }

        setCounterWrongLocation(0);
        upsertScannedItem(scannedSerial, { id: itemScanned.data.id, ...commonData });
        highlightRow(scannedSerial);
        setLocationStatus(false);
        if (physicalLocationStatus === 'Pending') {
          setPhysicalLocationStatus('Started');
        }
        focusAndClear();
      }
    } catch (error) {
      toast.error(`Something went wrong. ${error.response?.data?.error || error.message}`);
    }
  }

  async function handleLocationCheck(inputLocation) {
    const locationName = inputLocation.substring(inputLocation.indexOf(':') + 1);
    const locationData = await api.get(`/locations/getLocationByName/${locationName}`);

    if (!locationData.data) {
      errorMessage(
        'Invalid location',
        <div>
          <span style={{ fontWeight: 'bold', fontSize: 'large' }}>INVALID LOCATION</span>. Please, scan a level QR code.
        </div>
      );
      return;
    }

    const IS_SCANNED_LOCATION_FROM_PARENT = locationInfo.locationId === locationData.data.location_parent_id;

    if (!IS_SCANNED_LOCATION_FROM_PARENT) {
      errorMessage(
        'Scan a location level',
        <div>
          The bar code scanned is <span style={{ fontWeight: 'bold', fontSize: 'large' }}>NOT A LEVEL FROM THIS LOCATION</span>. This location has multiple
          levels. Please, scan a level QR code from this location.
        </div>
      );
      return;
    }

    setScannedLevel({ toLocationId: locationData.data.id, toLocationName: locationData.data.name });
    playAudio(successAudioFile);
    if (inputSearchRef.current) {
      inputSearchRef.current.value = '';
      inputSearchRef.current.focus();
    }
  }

  function handleSearchType(element) {
    if (element.key !== 'Enter') return;

    const elementScanned = element.target.value;
    const readingType = elementScanned.substring(0, elementScanned.indexOf(':'));

    if (isParentLocation) {
      if (readingType === 'LCT') {
        handleLocationCheck(elementScanned);
        return;
      }
      if (scannedLevel.toLocationName === '') {
        errorMessage(
          'Scan a location level',
          <div>
            The bar code scanned is <span style={{ fontWeight: 'bold', fontSize: 'large' }}>NOT A LOCATION LEVEL</span>. This location has multiple levels.
            Please, scan a level QR code.
          </div>
        );
        return;
      }
      handleScanSerialNumber(elementScanned);
      return;
    }

    if (readingType === 'LCT') {
      errorMessage(
        'Invalid Input',
        <div>
          The bar code scanned is a <span style={{ fontWeight: 'bold', fontSize: 'large' }}>LOCATION</span>. Please, scan again.
        </div>
      );
      return;
    }

    handleScanSerialNumber(elementScanned);
  }

  const handleCompleteLocation = async () => {
    const hasNotScannedItems = await api.get(`/physical_inventory/checkItemsNotScannedAtLocation/${match.params.physicalInventoryLocationId}`);

    const confirmAndComplete = () => {
      confirm({
        title: <span>Are you sure you want to complete scanning this location?</span>,
        icon: <ExclamationCircleFilled />,
        content: 'This action cannot be undone.',
        okText: `Yes, complete`,
        okType: 'success',
        cancelText: 'Cancel',
        cancelButtonProps: { type: 'danger' },
        autoFocusButton: 'cancel',
        async onOk() {
          await api.patch(`/physical_inventory/finishScanningLocation/${match.params.physicalInventoryLocationId}`);
          toast.success('Location complete');
          history.push(`/physicalInventoryLocations/${match.params.physicalInventoryId}`);
        },
        onCancel() {
          Modal.destroyAll();
        },
      });
    };

    if (hasNotScannedItems.data.status === 'nok') {
      confirm({
        title: <span>Some items were not scanned</span>,
        icon: <ExclamationCircleFilled />,
        content: `There are ${hasNotScannedItems.data.notScannedCount} items not scanned in this location. Do you want to complete this location anyway?`,
        okText: `Yes, I'm sure`,
        okType: 'success',
        cancelText: 'No, I will keep scanning',
        cancelButtonProps: { type: 'danger' },
        autoFocusButton: 'cancel',
        async onOk() {
          confirmAndComplete();
        },
        onCancel() {
          Modal.destroyAll();
        },
      });
    } else {
      confirmAndComplete();
    }
  };

  return (
    <Container>
      <SearchContainer>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Space>
            <Link to={`${returnUrl}/${match.params.physicalInventoryId}`}>
              <Button variant="contained" type="primary" icon={<ArrowLeftOutlined />}>
                Back
              </Button>
            </Link>
            {(inventoryManagerId === profile?.id || profile?.type === 999 || assignedTechnician === profile.name) && (
              <>
                <Button type="primary" onClick={handleCompleteLocation} disabled={locationStatus}>
                  Complete scanning this location
                </Button>

                {!disableSerialNumberInput && physicalLocationStatus === 'Pending' && (
                  <Button
                    type="primary"
                    onClick={() => {
                      confirm({
                        title: <span>Are you sure you want to mark this location as &quot;Nothing changed since last inventory&quot;?</span>,
                        icon: <ExclamationCircleFilled />,
                        content: 'This action cannot be undone.',
                        okText: `Yes, mark as nothing changed`,
                        okType: 'success',
                        cancelText: 'Cancel',
                        cancelButtonProps: { type: 'danger' },
                        autoFocusButton: 'cancel',
                        async onOk() {
                          try {
                            await api.patch(`/physical_inventory/markNothingChanged/${match.params.physicalInventoryLocationId}`);
                            toast.success("Marked as 'Nothing changed since last inventory'.");
                            history.push(`/physicalInventoryLocations/${match.params.physicalInventoryId}`);
                          } catch (error) {
                            toast.error(`Failed to mark as nothing changed. ${error.response?.data?.error || error.message}`);
                          }
                        },
                        onCancel() {
                          Modal.destroyAll();
                        },
                      });
                    }}
                  >
                    Mark as &quot;Nothing changed since last inventory&quot;
                  </Button>
                )}
                {locationStatus === true && (
                  <Button
                    type="primary"
                    onClick={async () => {
                      try {
                        await api.patch(`/physical_inventory/reopenLocation/${match.params.physicalInventoryLocationId}`);
                        toast.success('Location re-opened.');
                        history.push(`/physicalInventoryLocations/${match.params.physicalInventoryId}`);
                      } catch (e) {
                        toast.error('Failed to re-open location.');
                      }
                    }}
                  >
                    Re-open location
                  </Button>
                )}
              </>
            )}
          </Space>
          <Title level={2}>{locationInfo?.locationDescription}</Title>
          <div />
        </div>

        <Divider />

        <SearchSummary>
          <input
            placeholder="Scan label"
            type="text"
            id="inputSearch"
            name="inputSearch"
            onKeyDown={handleSearchType}
            ref={inputSearchRef}
            style={{ marginRight: '20px' }}
            disabled={disableSerialNumberInput}
          />
          <Tooltip title="Show or hide items that were not scanned yet. When enabled, the list reloads including not-scanned units.">
            <Space size={6} align="center" style={{ padding: '4px 10px', borderRadius: 8, background: '#f7faff', border: '1px solid #d6e9ff' }}>
              <Switch checked={includeNotScanned} onChange={setIncludeNotScanned} loading={isLoadingItems} />
              <span style={{ fontWeight: 600 }}>Show Not Scanned</span>
            </Space>
          </Tooltip>
          <Tag>Total: {itemsList.length}</Tag>
          <Tag color="blue">Scanned: {scannedCount}</Tag>
          <Tag color="cyan">Not Scanned: {itemsList.length - scannedCount}</Tag>
          {includeNotScanned && <Tag color="gold">View: All items (including Not Scanned)</Tag>}
          {hasStatusSummary && ' By Status: \u00A0 '}
          {searchSummary && Object.keys(searchSummary).map((el) => <Tag key={el} color={statusColor[el]}>{`${el}: ${searchSummary[el]}`}</Tag>)}
        </SearchSummary>
      </SearchContainer>

      <Divider />

      {itemsList.length > 0 ? (
        <ContainerForTables>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 24, width: '100%' }}>
            <div style={{ flex: 2, minWidth: 0 }}>
              {isParentLocation && (
                <Title level={2}>
                  Level being scanned: <span style={{ color: 'red' }}>{scannedLevel.toLocationName}</span>
                </Title>
              )}
              <TransactionContainer>
                <VirtualTable
                  ref={virtualTableRef}
                  columns={columns}
                  dataSource={itemsList}
                  rowKey="serial_number"
                  rowClassName={getRowClassName}
                  loading={isLoadingItems}
                />
              </TransactionContainer>
              <Title level={4} style={{ marginTop: 16 }}>
                Assigned Technician: {assignedTechnician}
              </Title>
              {levelSummary.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <TransactionTable>
                    <thead>
                      <tr>
                        <th>Level</th>
                        <th>Scanned</th>
                        <th>RBMS Location</th>
                      </tr>
                    </thead>
                    <tbody>
                      {levelSummary.map((level) => (
                        <tr key={level.name} id={level.name}>
                          <td>{level.name || 'no Location'}</td>
                          <td>{level.scanned}</td>
                          <td>{level.rbms}</td>
                        </tr>
                      ))}
                    </tbody>
                  </TransactionTable>
                </div>
              )}
            </div>
            {timesScannedSummary.length > 0 && (
              <div style={{ flex: 1, minWidth: 220, maxWidth: 340, maxHeight: `calc(100vh - 350px)`, overflowY: 'auto', marginBottom: 8 }}>
                <Title level={5} style={{ marginBottom: 8 }}>
                  Scanned Multiple Times
                </Title>
                <TransactionTable>
                  <thead>
                    <tr>
                      <th>Serial Number</th>
                      <th>Times Scanned</th>
                    </tr>
                  </thead>
                  <tbody>
                    {timesScannedSummary.map((unit) => (
                      <tr key={unit.serial_number} id={unit.serial_number}>
                        <td>{unit.serial_number}</td>
                        <td>{unit.times_scanned}</td>
                      </tr>
                    ))}
                  </tbody>
                </TransactionTable>
              </div>
            )}
          </div>
        </ContainerForTables>
      ) : (
        <Empty description="There is nothing to show" />
      )}
    </Container>
  );
}
